import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from '@/components/ui';
import { MessageSquare, Save, Edit3, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { commentAPI } from '../services/api';

interface CommentSectionProps {
  emailId: string;
}

interface CommentData {
  email_id: string;
  comment: string | null;
  include_in_ai: boolean;
}

export const CommentSection: React.FC<CommentSectionProps> = ({ emailId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [includeInAi, setIncludeInAi] = useState(false);
  
  const queryClient = useQueryClient();
  
  const { data: commentData } = useQuery({
    queryKey: ['comment', emailId],
    queryFn: async () => {
      const response = await commentAPI.get(emailId);
      return response.data;
    }
  });
  
  // Update local state when data changes
  useEffect(() => {
    if (commentData) {
      setCommentText(commentData.comment || '');
      setIncludeInAi(commentData.include_in_ai);
    }
  }, [commentData]);
  
  const updateMutation = useMutation({
    mutationFn: async ({ comment, includeInAi }: { comment: string; includeInAi: boolean }) => {
      const response = await commentAPI.update(emailId, comment, includeInAi);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comment', emailId] });
      setIsEditing(false);
      toast.success('Comment saved successfully');
    },
    onError: () => {
      toast.error('Failed to save comment');
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await commentAPI.delete(emailId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comment', emailId] });
      setCommentText('');
      setIncludeInAi(false);
      setIsEditing(false);
      toast.success('Comment deleted');
    },
    onError: () => {
      toast.error('Failed to delete comment');
    }
  });
  
  const handleSave = () => {
    if (commentText.trim()) {
      updateMutation.mutate({ comment: commentText.trim(), includeInAi });
    }
  };
  
  const handleDelete = () => {
    deleteMutation.mutate();
  };
  
  const hasComment = commentData?.comment && commentData.comment.trim();
  
  return (
    <div className="border border-amber-200/80 rounded-xl p-3.5 bg-amber-50/40 shadow-xs transition-all">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center space-x-1.5 text-xs font-bold text-amber-900">
          <MessageSquare className="w-3.5 h-3.5 text-amber-600" />
          <span>Internal Ticket Notes</span>
        </div>
        {hasComment && !isEditing && (
          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100/80 px-1.5 py-0.5 rounded">
            Saved Note
          </span>
        )}
      </div>

      {!isEditing && !hasComment && (
        <button
          onClick={() => setIsEditing(true)}
          className="w-full py-2 border border-dashed border-amber-300 rounded-lg text-xs font-medium text-amber-800 bg-white hover:bg-amber-100/50 hover:border-amber-400 transition-colors flex items-center justify-center space-x-1.5 cursor-pointer"
        >
          <Edit3 className="w-3.5 h-3.5 text-amber-600" />
          <span>+ Add Internal Note</span>
        </button>
      )}

      {!isEditing && hasComment && (
        <div className="space-y-2.5">
          <div className="bg-white p-3 rounded-lg border border-amber-200/60 text-xs text-slate-800 leading-relaxed font-sans whitespace-pre-wrap shadow-2xs">
            {commentData?.comment}
          </div>

          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center space-x-1.5">
              <Checkbox
                id={`include-ai-${emailId}`}
                checked={commentData?.include_in_ai || false}
                onCheckedChange={(checked: boolean) => {
                  setIncludeInAi(checked);
                  updateMutation.mutate({ 
                    comment: commentData?.comment || '', 
                    includeInAi: checked 
                  });
                }}
              />
              <label htmlFor={`include-ai-${emailId}`} className="text-[11px] text-slate-600 cursor-pointer select-none">
                Use in AI Context
              </label>
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => {
                  setCommentText(commentData?.comment || '');
                  setIsEditing(true);
                }}
                className="p-1 text-slate-500 hover:text-amber-700 rounded transition-colors text-xs flex items-center space-x-1"
                title="Edit note"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleDelete}
                className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors text-xs"
                title="Delete note"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditing && (
        <div className="space-y-2.5">
          <Textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Write internal notes about this email or customer issue..."
            className="min-h-[85px] text-xs bg-white border-amber-300 focus:border-amber-500 focus:ring-amber-200 font-sans"
          />

          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center space-x-1.5">
              <Checkbox
                id={`include-ai-edit-${emailId}`}
                checked={includeInAi}
                onCheckedChange={(checked: boolean) => setIncludeInAi(checked)}
              />
              <label htmlFor={`include-ai-edit-${emailId}`} className="text-[11px] text-slate-600 cursor-pointer select-none">
                Use in AI Context
              </label>
            </div>

            <div className="flex items-center space-x-1.5">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setCommentText(commentData?.comment || '');
                  setIncludeInAi(commentData?.include_in_ai || false);
                }}
                className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!commentText.trim() || updateMutation.isPending}
                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-semibold shadow-xs flex items-center space-x-1 disabled:opacity-50 cursor-pointer"
              >
                <Save className="w-3 h-3" />
                <span>Save Note</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
